const SAFE_HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const SHADOW_EFFECT_LIMITS = Object.freeze({
  offset: 200,
  blur: 400,
  spread: 200,
  opacity: 100,
});

export const DEFAULT_SHADOW_EFFECT = Object.freeze({
  enabled: false,
  color: "#0f172a",
  opacity: 18,
  offsetX: 0,
  offsetY: 8,
  blur: 24,
  spread: 0,
});

const clampNumber = (value, min, max, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.min(max, Math.max(min, numeric));
};

const round = (value) => Math.round(value * 100) / 100;

export const normalizeEffectColor = (
  value,
  fallback = DEFAULT_SHADOW_EFFECT.color
) => {
  const candidate = String(value || "").trim();
  if (SAFE_HEX_COLOR.test(candidate)) {
    if (candidate.length === 4) {
      return `#${candidate[1]}${candidate[1]}${candidate[2]}${candidate[2]}${candidate[3]}${candidate[3]}`.toLowerCase();
    }
    return candidate.toLowerCase();
  }
  return SAFE_HEX_COLOR.test(fallback)
    ? normalizeEffectColor(fallback, DEFAULT_SHADOW_EFFECT.color)
    : DEFAULT_SHADOW_EFFECT.color;
};

export const normalizeShadowEffect = (value = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};

  return {
    enabled: source.enabled === true,
    color: normalizeEffectColor(source.color, DEFAULT_SHADOW_EFFECT.color),
    opacity: round(
      clampNumber(
        source.opacity,
        0,
        SHADOW_EFFECT_LIMITS.opacity,
        DEFAULT_SHADOW_EFFECT.opacity
      )
    ),
    offsetX: round(
      clampNumber(
        source.offsetX,
        -SHADOW_EFFECT_LIMITS.offset,
        SHADOW_EFFECT_LIMITS.offset,
        DEFAULT_SHADOW_EFFECT.offsetX
      )
    ),
    offsetY: round(
      clampNumber(
        source.offsetY,
        -SHADOW_EFFECT_LIMITS.offset,
        SHADOW_EFFECT_LIMITS.offset,
        DEFAULT_SHADOW_EFFECT.offsetY
      )
    ),
    blur: round(
      clampNumber(
        source.blur,
        0,
        SHADOW_EFFECT_LIMITS.blur,
        DEFAULT_SHADOW_EFFECT.blur
      )
    ),
    spread: round(
      clampNumber(
        source.spread,
        -SHADOW_EFFECT_LIMITS.spread,
        SHADOW_EFFECT_LIMITS.spread,
        DEFAULT_SHADOW_EFFECT.spread
      )
    ),
  };
};

const hexToRgb = (color) => {
  const normalized = normalizeEffectColor(color).slice(1);
  return {
    red: Number.parseInt(normalized.slice(0, 2), 16),
    green: Number.parseInt(normalized.slice(2, 4), 16),
    blue: Number.parseInt(normalized.slice(4, 6), 16),
  };
};

export const buildShadowCssValue = (value) => {
  const shadow = normalizeShadowEffect(value);
  if (!shadow.enabled) return "none";
  const { red, green, blue } = hexToRgb(shadow.color);
  const alpha = round(shadow.opacity / 100);
  return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.spread}px rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export const sanitizeLegacyBoxShadow = (value) => {
  const candidate = String(value || "").trim().slice(0, 320);
  if (!candidate || /[;{}]|url\s*\(|expression\s*\(/i.test(candidate)) return "";
  return candidate;
};

export const normalizeVisualEffects = (value = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
  const normalized = { ...source };
  if (Object.prototype.hasOwnProperty.call(source, "shadow")) {
    normalized.shadow = normalizeShadowEffect(source.shadow);
  }
  return normalized;
};

export const buildVisualEffectsStyle = (
  visualEffects,
  { legacyBoxShadow = "" } = {}
) => {
  const source =
    visualEffects && typeof visualEffects === "object" && !Array.isArray(visualEffects)
      ? visualEffects
      : null;
  if (source && Object.prototype.hasOwnProperty.call(source, "shadow")) {
    return { boxShadow: buildShadowCssValue(source.shadow) };
  }

  const legacy = sanitizeLegacyBoxShadow(legacyBoxShadow);
  return legacy ? { boxShadow: legacy } : {};
};

export const updateVisualEffect = (visualEffects, key, value) => ({
  ...(visualEffects && typeof visualEffects === "object" ? visualEffects : {}),
  [key]: value,
});

export const removeVisualEffect = (visualEffects, key) => {
  const next = {
    ...(visualEffects && typeof visualEffects === "object" ? visualEffects : {}),
  };
  delete next[key];
  return next;
};
